import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  ResultsPageComparisonConfig,
  ResultsPageConfig,
  ResultsPageModalCopy,
} from '@de/types/types';
import { DEFAULT_RESULTS_PAGE_CONFIG } from '@de/types/types';
import {
  DEFAULT_LOADING_COPY,
  type LoadingCopy,
} from '../components/Loading/defaults';
import { normalizeComparisonBounds } from '../utils/comparisonBounds';

export {
  DEFAULT_COMPARISON_MAX_SELECTION,
  DEFAULT_COMPARISON_MIN_SELECTION,
} from '../utils/comparisonBounds';

export type ResultsPageComparisonCopy = Pick<
  ResultsPageComparisonConfig,
  | 'bannerText'
  | 'ctaText'
  | 'heading'
  | 'logo'
  | 'title'
  | 'subtitle'
  | 'buttonText'
  | 'placeholderText'
>;

export type ResultsPageLoadingCopy = LoadingCopy;

export interface ResultsPageConfigContextValue {
  config: ResultsPageConfig;
  shouldShowLoadingInterstitial: boolean;
  shouldShowRecommenderModal: boolean;
  shouldShowSpotlightModal: boolean;
  shouldShowComparison: boolean;
  shouldShowSubmissions: boolean;
  /** Omitted, or `enabled` not `false`: show the post-submit thank-you step. */
  shouldShowThankYou: boolean;
  comparisonMinSelection: number;
  comparisonMaxSelection: number;
  getRecommenderModalCopy: () => ResultsPageModalCopy;
  getSpotlightModalCopy: () => ResultsPageModalCopy;
  getLoadingCopy: () => ResultsPageLoadingCopy;
  getComparisonCopy: () => ResultsPageComparisonCopy;
  getComparisonBounds: () => { minItems: number; maxItems: number };
  getComparisonConfig: () => ResultsPageComparisonConfig | undefined;
  getSubmissionsCopy: () => ResultsPageModalCopy;
}

export interface ResultsPageConfigProviderProps {
  config?: ResultsPageConfig;
  children: React.ReactNode;
}

const ResultsPageConfigContext = createContext<
  ResultsPageConfigContextValue | undefined
>(undefined);

/**
 * Runtime visibility of the platform-owned Recommender / Spotlight
 * popup, surfaced separately from the (static) results-page config so
 * config consumers don't re-render when the popup toggles.
 *
 * Why this exists: the popup modal occludes the entire
 * background DSL result tree, but an `IntersectionObserver` is purely
 * geometric — it reports the background cards as "intersecting" even
 * while they're hidden behind the modal overlay. `Trackable` reads
 * `isPopupOpen` to defer its intersect-driven view events until the
 * popup is dismissed and the cards are genuinely revealed, instead of
 * firing `ProductViewed` against an occluded card and burning the
 * fire-once dedup budget.
 */
export interface ResultsPagePopupVisibilityContextValue {
  /** `true` while the Recommender / Spotlight popup modal is open. */
  readonly isPopupOpen: boolean;
  /** Set by `DslResultsPagePopups` as its modal opens / closes. */
  readonly setPopupOpen: (open: boolean) => void;
}

const ResultsPagePopupVisibilityContext = createContext<
  ResultsPagePopupVisibilityContextValue | undefined
>(undefined);

const DEFAULT_POPUP_VISIBILITY: ResultsPagePopupVisibilityContextValue = {
  isPopupOpen: false,
  setPopupOpen: () => {},
};

export const ResultsPageConfigProvider: React.FC<
  ResultsPageConfigProviderProps
> = ({ config, children }) => {
  const value = useMemo(
    () => buildResultsPageConfigValue(config ?? DEFAULT_RESULTS_PAGE_CONFIG),
    [config],
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // `useState`'s setter identity is already stable; wrap it so the
  // public surface name stays decoupled from the internal state hook.
  const setPopupOpen = useCallback((open: boolean) => {
    setIsPopupOpen(open);
  }, []);
  const popupVisibility = useMemo<ResultsPagePopupVisibilityContextValue>(
    () => ({ isPopupOpen, setPopupOpen }),
    [isPopupOpen, setPopupOpen],
  );

  return (
    <ResultsPageConfigContext.Provider value={value}>
      <ResultsPagePopupVisibilityContext.Provider value={popupVisibility}>
        {children}
      </ResultsPagePopupVisibilityContext.Provider>
    </ResultsPageConfigContext.Provider>
  );
};

/**
 * Read the results-page popup visibility. Returns a no-op default
 * (`isPopupOpen: false`) when mounted outside a
 * `ResultsPageConfigProvider` (e.g. non-result DSL steps, legacy
 * render paths, unit tests) so `Trackable` keeps its prior semantics
 * everywhere except a result page with an open popup.
 */
export function useResultsPagePopupVisibility(): ResultsPagePopupVisibilityContextValue {
  return (
    useContext(ResultsPagePopupVisibilityContext) ?? DEFAULT_POPUP_VISIBILITY
  );
}

export function useResultsPageConfig(): ResultsPageConfigContextValue {
  const context = useContext(ResultsPageConfigContext);
  if (!context) {
    return DEFAULT_RESULTS_PAGE_CONFIG_VALUE;
  }
  return context;
}

export function useResultsPageConfigOptional():
  | ResultsPageConfigContextValue
  | undefined {
  return useContext(ResultsPageConfigContext);
}

function buildResultsPageConfigValue(
  config: ResultsPageConfig,
): ResultsPageConfigContextValue {
  const recommenderModal = config.recommender?.modal;
  const spotlightModal = config.spotlight?.modal;
  const comparison = config.comparison;
  const loading = config.loading;
  const { minItems: comparisonMinSelection, maxItems: comparisonMaxSelection } =
    normalizeComparisonBounds({
      minItems: comparison?.minSelection,
      maxItems: comparison?.maxSelection,
    });

  const submissions = config.submissions;

  return {
    config,
    shouldShowLoadingInterstitial: loading?.enabled === true,
    shouldShowRecommenderModal: recommenderModal?.enabled === true,
    shouldShowSpotlightModal: spotlightModal?.enabled === true,
    shouldShowComparison: config.comparison?.enabled === true,
    shouldShowSubmissions: submissions?.enabled === true,
    shouldShowThankYou: config.exitStrategy?.thankYou?.enabled !== false,
    comparisonMinSelection,
    comparisonMaxSelection,
    getRecommenderModalCopy: () => ({
      title: recommenderModal?.title,
      subtitle: recommenderModal?.subtitle,
      disclosureTitle: recommenderModal?.disclosureTitle,
      disclosureContent: recommenderModal?.disclosureContent,
    }),
    getSpotlightModalCopy: () => ({
      title: spotlightModal?.title,
      subtitle: spotlightModal?.subtitle,
      disclosureTitle: spotlightModal?.disclosureTitle,
      disclosureContent: spotlightModal?.disclosureContent,
    }),
    getLoadingCopy: () => ({
      title: loading?.title ?? DEFAULT_LOADING_COPY.title,
      statusLabel: loading?.statusLabel ?? DEFAULT_LOADING_COPY.statusLabel,
      steps:
        loading?.steps && loading.steps.length > 0
          ? loading.steps
          : DEFAULT_LOADING_COPY.steps,
    }),
    getComparisonCopy: () => ({
      bannerText: comparison?.bannerText,
      ctaText: comparison?.ctaText,
      heading: comparison?.heading,
      logo: comparison?.logo,
      title: comparison?.title,
      subtitle: comparison?.subtitle,
      buttonText: comparison?.buttonText,
      placeholderText: comparison?.placeholderText,
    }),
    getComparisonBounds: () => ({
      minItems: comparisonMinSelection,
      maxItems: comparisonMaxSelection,
    }),
    getComparisonConfig: () => comparison,
    getSubmissionsCopy: () => ({
      title: submissions?.title,
      subtitle: submissions?.subtitle,
    }),
  };
}

const DEFAULT_RESULTS_PAGE_CONFIG_VALUE = buildResultsPageConfigValue(
  DEFAULT_RESULTS_PAGE_CONFIG,
);
